import { useEffect, useRef, useState } from 'react';
import { openDataSettings } from '../lib/settingsEvents.js';
import { Modal } from './Modal.js';

/**
 * ehr#458 (PROGRESS_NOTE_10-14-V81, "the mic on every box"): a mic in a note box that dictates into
 * that box. It follows the same rules as NotePilot's session-summary mic: the practice turns
 * dictation on (Data & Settings → Clinical notes: the browser's speech service hears the audio), a
 * client who opted out of AI listening turns it off, and a browser without speech recognition says
 * so. One mic listens at a time: starting another stops the first.
 */
export interface DictateOpts {
  /** The practice's Clinical notes switch. */
  enabled: boolean;
  /** reminders#4: the client opted out of AI listening. */
  aiOptOut: boolean;
  /** ehr#458: an admin or manager, who can turn dictation on (Data & settings → Clinical notes). */
  canTurnOn?: boolean;
}

/** Why a mic can't listen: the practice's switch, the client's opt-out, or the browser. */
export type DictationOff = 'off' | 'optout' | 'unsupported';

/**
 * ehr#458 (Chase 10-16): a click on a mic that can't listen explains why in a window (the line
 * under the summary was cut off). With dictation off, an admin or manager gets the way to the
 * setting; everyone else is told who can turn it on.
 */
export function DictationOffWindow({
  reason,
  canTurnOn,
  onClose,
}: {
  reason: DictationOff;
  canTurnOn: boolean;
  onClose: () => void;
}) {
  const title =
    reason === 'optout'
      ? 'Dictation is off for this client'
      : reason === 'unsupported'
        ? 'Dictation isn’t available in this browser'
        : 'Dictation is off';
  const admin = reason === 'off' && canTurnOn;
  return (
    <Modal
      title={title}
      onClose={onClose}
      className="pn-dict-modal"
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
          {admin && (
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                onClose();
                openDataSettings('clinical');
              }}
            >
              Open Clinical notes settings
            </button>
          )}
        </>
      }
    >
      {reason === 'off' && (
        <>
          <p className="pn-modal-p">
            Dictation is off for this practice. When it&rsquo;s on, what you say is typed into the
            box; the audio goes to your browser&rsquo;s speech service (Google in Chrome, Apple in
            Safari).
          </p>
          <p className="pn-modal-p">
            {admin
              ? 'You can turn it on in Data & settings → Clinical notes. This note picks it up when you save there.'
              : 'An admin can turn it on in Data & settings → Clinical notes.'}
          </p>
        </>
      )}
      {reason === 'optout' && (
        <p className="pn-modal-p">
          This client opted out of AI listening, so dictation stays off on their notes. It&rsquo;s
          set in Data & settings → Opt-Out Center.
        </p>
      )}
      {reason === 'unsupported' && (
        <p className="pn-modal-p">Try Chrome, Edge or Safari to dictate.</p>
      )}
    </Modal>
  );
}

interface SpeechResultList {
  length: number;
  [i: number]: { isFinal: boolean; 0: { transcript: string } };
}
interface SpeechRec {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((e: { resultIndex: number; results: SpeechResultList }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}
type SpeechRecCtor = new () => SpeechRec;
const speechCtor = (): SpeechRecCtor | null => {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecCtor;
    webkitSpeechRecognition?: SpeechRecCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};

/** The mic that is listening now, so another one can stop it. */
let active: { stop: () => void } | null = null;

export const DICTATION_OFF =
  'Dictation is off for this practice. An admin can turn it on in Data & Settings → Clinical notes.';
const AI_OFF = 'This client opted out of AI listening (Data & Settings → Opt-Out Center).';
const NO_SPEECH = 'Dictation is not supported in this browser. Try Chrome, Edge or Safari.';

/**
 * ehr#523 (Jay 10-18): hold Right Alt in a box to dictate, let go to stop. What one key event does:
 * `arm` (Right Alt went down: listen once it's held a moment), `release` (Right Alt came up),
 * `cancel` (another key while it's held, as AltGr + a key types a character on many keyboards, so
 * that never dictates), or nothing. A held key's repeats do nothing.
 */
export const PTT_CODE = 'AltRight';
/** How long Right Alt is held before listening starts (a quick AltGr + key never gets there). */
export const PTT_HOLD_MS = 250;
export type PttAction = 'arm' | 'release' | 'cancel' | null;
export function pttAction(
  type: 'keydown' | 'keyup',
  e: { code: string; repeat?: boolean },
  held: boolean,
): PttAction {
  if (e.code === PTT_CODE) {
    if (type === 'keyup') return held ? 'release' : null;
    return held || e.repeat ? null : 'arm';
  }
  return type === 'keydown' && held ? 'cancel' : null;
}
export const PTT_HINT = 'Hold Right Alt to dictate.';

/** Text heard, joined onto what the box already has. */
export function appendHeard(current: string, heard: string): string {
  const t = heard.trim();
  if (!t) return current;
  const sep = current && !/\s$/.test(current) ? ' ' : '';
  return current + sep + t;
}

/** The box a mic sits in (its .pn-ta-wrap), and the text field there. */
const wrapOf = (btn: HTMLElement): HTMLElement | null =>
  btn.closest<HTMLElement>('.pn-ta-wrap') ?? btn.parentElement;
const fieldOf = (btn: HTMLElement | null): HTMLTextAreaElement | HTMLInputElement | null => {
  const w = btn ? wrapOf(btn) : null;
  return w?.querySelector<HTMLTextAreaElement | HTMLInputElement>('textarea, input') ?? null;
};

export function DictateMic({
  dictate,
  label,
  value,
  onText,
}: {
  dictate: DictateOpts;
  /** The box's name, for the button's label. */
  label: string;
  /** The box's text now (heard text is added after it). */
  value: string;
  onText: (next: string) => void;
}) {
  const [listening, setListening] = useState(false);
  const [msg, setMsg] = useState('');
  const recRef = useRef<SpeechRec | null>(null);
  const on = useRef(false);
  // the latest text, so each heard phrase goes after the one before it
  const valueRef = useRef(value);
  valueRef.current = value;
  const Speech = typeof window === 'undefined' ? null : speechCtor();

  const btnRef = useRef<HTMLButtonElement>(null);
  // ehr#523: Right Alt push-to-talk: held now, its start timer, and whether it started this listen
  const ptt = useRef<{ held: boolean; timer: number | null; started: boolean }>({
    held: false,
    timer: null,
    started: false,
  });
  const pttReset = () => {
    const p = ptt.current;
    if (p.timer !== null) window.clearTimeout(p.timer);
    ptt.current = { held: false, timer: null, started: false };
  };

  const stop = (why = '') => {
    pttReset();
    on.current = false;
    const r = recRef.current;
    if (r) {
      r.onend = null;
      try {
        r.stop();
      } catch {
        /* already stopped */
      }
    }
    recRef.current = null;
    if (active === me.current) active = null;
    setListening(false);
    setMsg(why);
  };
  const stopRef = useRef(stop);
  stopRef.current = stop;
  // this mic, as the one listening (`active`)
  const me = useRef({ stop: () => stopRef.current() });
  useEffect(() => () => stopRef.current(), []);

  const start = () => {
    if (!Speech) return;
    // ehr#523: only another mic; this one's own stop would end a Right Alt hold as it starts
    if (active && active !== me.current) active.stop();
    const rec = new Speech();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    rec.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i]!;
        // ehr#910: onto what the box shows now (a note box can be ahead of the note's copy)
        if (r.isFinal)
          onText(appendHeard(fieldOf(btnRef.current)?.value ?? valueRef.current, r[0].transcript));
        else interim += r[0].transcript;
      }
      setMsg(interim ? `Listening: ${interim}` : 'Listening…');
    };
    rec.onerror = (e) =>
      stop(
        'Dictation stopped: ' +
          (e.error === 'not-allowed' ? 'microphone access was blocked' : e.error),
      );
    rec.onend = () => {
      if (on.current)
        try {
          rec.start();
        } catch {
          /* restarting too fast */
        }
    };
    recRef.current = rec;
    rec.start();
    on.current = true;
    active = me.current;
    setListening(true);
    setMsg('Listening…');
  };

  const [off, setOff] = useState<DictationOff | null>(null);
  const offReason: DictationOff | null = dictate.aiOptOut
    ? 'optout'
    : !dictate.enabled
      ? 'off'
      : !Speech
        ? 'unsupported'
        : null;
  const why = dictate.aiOptOut
    ? AI_OFF
    : !dictate.enabled
      ? DICTATION_OFF
      : !Speech
        ? NO_SPEECH
        : '';
  const toggle = () => {
    if (offReason) return setOff(offReason);
    if (on.current) stop();
    else {
      start();
      // ehr#523: the box keeps the focus, so leaving it (a click outside, Tab) stops the mic
      const f = fieldOf(btnRef.current);
      if (f && document.activeElement !== f) f.focus({ preventScroll: true });
    }
  };
  // the latest start/offReason, for the listeners below (bound once)
  const live = useRef({ start, offReason });
  live.current = { start, offReason };

  useEffect(() => {
    const btn = btnRef.current;
    const wrap = btn ? wrapOf(btn) : null;
    if (!btn || !wrap) return;
    // ehr#523 part 1: focus leaving the box (and its mic) stops the recording. Switching to another
    // window or app keeps it (the page itself lost focus), as the browser's mic prompt would too.
    let check: number | null = null;
    const onFocusOut = () => {
      if (check !== null) window.clearTimeout(check);
      check = window.setTimeout(() => {
        check = null;
        if (!on.current || !document.hasFocus()) return;
        if (wrap.contains(document.activeElement)) return;
        stopRef.current();
      }, 0);
    };
    // ehr#523 part 2: hold Right Alt to dictate; only in a box whose mic can listen, so AltGr typing
    // elsewhere (and here, with dictation off) is untouched.
    const onKey = (e: KeyboardEvent) => {
      const p = ptt.current;
      const act = pttAction(e.type === 'keyup' ? 'keyup' : 'keydown', e, p.held);
      if (!act) {
        if (e.code === PTT_CODE && p.held) e.preventDefault(); // a held key's repeats
        return;
      }
      if (act === 'arm') {
        const f = fieldOf(btn);
        if (live.current.offReason || !f || f.readOnly || f.disabled) return;
        // AltGr (key "AltGraph") is left to type; a plain Right Alt would open the browser's menu
        if (e.key !== 'AltGraph') e.preventDefault();
        p.held = true;
        p.timer = window.setTimeout(() => {
          p.timer = null;
          if (!p.held || on.current) return;
          live.current.start();
          p.started = true;
        }, PTT_HOLD_MS);
        return;
      }
      const started = p.started;
      if (act === 'release') e.preventDefault();
      pttReset();
      if (started && on.current) stopRef.current();
    };
    // Right Alt let go in another window never reaches the box: the page losing focus lets go.
    const onWinBlur = () => {
      const p = ptt.current;
      if (!p.held) return;
      const started = p.started;
      pttReset();
      if (started && on.current) stopRef.current();
    };
    wrap.addEventListener('focusout', onFocusOut);
    wrap.addEventListener('keydown', onKey);
    wrap.addEventListener('keyup', onKey);
    window.addEventListener('blur', onWinBlur);
    return () => {
      if (check !== null) window.clearTimeout(check);
      window.removeEventListener('blur', onWinBlur);
      wrap.removeEventListener('focusout', onFocusOut);
      wrap.removeEventListener('keydown', onKey);
      wrap.removeEventListener('keyup', onKey);
    };
  }, []);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`pn-mic${listening ? ' on' : ''}${why ? ' off' : ''}`}
        aria-pressed={listening}
        aria-label={listening ? `Stop dictating into ${label}` : `Dictate into ${label}`}
        title={
          why ||
          (listening
            ? 'Stop dictation. Leaving the box stops it too.'
            : `Dictate (audio goes to your browser's speech service). ${PTT_HINT}`)
        }
        // ehr#523: a click on the mic leaves the focus in the box, so it doesn't count as leaving it
        onMouseDown={(e) => e.preventDefault()}
        onClick={toggle}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <rect x="9" y="2" width="6" height="12" rx="3" />
          <path d="M5 10a7 7 0 0 0 14 0M12 17v5" />
        </svg>
      </button>
      {msg && (
        <span className="pn-mic-msg" role="status">
          {msg}
        </span>
      )}
      {off && (
        <DictationOffWindow
          reason={off}
          canTurnOn={Boolean(dictate.canTurnOn)}
          onClose={() => setOff(null)}
        />
      )}
    </>
  );
}
